import {
  FiUsers,
  FiGift,
  FiUserCheck,
  FiChevronDown,
  FiHome,
  FiGrid,
  FiCircle,
  FiLogOut,
  FiArrowUpCircle,
  FiArrowDownCircle,
} from "react-icons/fi";
import { NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/axios";

const menu = [
  { label: "Home", icon: FiHome, to: "/" },
  { label: "Back Office", icon: FiGrid, to: "/backoffice" },
  { label: "Agents", icon: FiUsers, to: "/agents" },
  { label: "Player", icon: FiUsers, to: "/users" },
  { label: "Recharge List", icon: FiArrowUpCircle, to: "/recharge-list" },
  { label: "Withdrawal List", icon: FiArrowDownCircle, to: "/withdrawal-list" },
  { label: "Bounce Reward", icon: FiGift, to: "/bounce-reward" },
  {
    label: "Report",
    icon: FiGrid,
    children: [
      { label: "Win/Loss Report Player", icon: FiCircle, to: "/win-loss-report" },
      { label: "Daily Report", icon: FiCircle, to: "/daily-report" },
      { label: "Betting History", icon: FiCircle, to: "/betting-history" },
      { label: "Point Transaction Report", icon: FiCircle, to: "/point-transaction-report" },
      { label: "Refer Point History", icon: FiCircle, to: "/refer-point-history" },
      { label: "Transaction Report", icon: FiCircle, to: "/transaction-report" },
      { label: "Wallet Transaction Report", icon: FiCircle, to: "/wallet-transaction-report" },
      { label: "Login Report", icon: FiCircle, to: "/login-report" },
    ]
  },
  {
    label: "Betfair",
    icon: FiGrid,
    children: [
      { label: "Betfair Bet History", icon: FiCircle, to: "/betfair-bet-history" },
      { label: "Betfair Bet History(M)", icon: FiCircle, to: "/betfair-bet-history-m" },
      { label: "Settlement", icon: FiCircle, to: "/betfair-settlement" },
    ]
  },
  { label: "Admin", icon: FiUserCheck, to: "/admin" },
  { label: "Payment Methods", icon: FiGrid, to: "/settings/payments" },
  { label: "Settings", icon: FiGrid, to: "/settings/general" },
];

/* ─── color tokens ─── */
const GREEN = "#fff";        /* active text */
const GREEN_BG = "#007bff";     /* active background */
const TEXT_PRIMARY = "#b8c7ce"; /* nav label */
const TEXT_MUTED = "#b8c7ce";   /* icon / subtitle */
const BORDER = "#1a2226";       /* dividers */
const BG = "#343a40";           /* sidebar bg */

const avatarSvg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <circle cx="50" cy="50" r="50" fill="#E2E8F0"/>
  <path d="M25 85 C25 70, 75 70, 75 85 Z" fill="#4A5568"/>
  <path d="M40 70 L50 80 L60 70 Z" fill="#E2E8F0"/>
  <circle cx="50" cy="45" r="22" fill="#EDC5A5"/>
  <path d="M28 45 C28 25, 72 25, 72 45 C65 30, 35 30, 28 45 Z" fill="#5C4033"/>
  <rect x="33" y="40" width="14" height="8" rx="2" fill="none" stroke="#1A202C" stroke-width="2.5"/>
  <rect x="53" y="40" width="14" height="8" rx="2" fill="none" stroke="#1A202C" stroke-width="2.5"/>
  <line x1="47" y1="44" x2="53" y2="44" stroke="#1A202C" stroke-width="2.5"/>
  <line x1="30" y1="44" x2="33" y2="44" stroke="#1A202C" stroke-width="1.5"/>
  <line x1="67" y1="44" x2="70" y2="44" stroke="#1A202C" stroke-width="1.5"/>
  <circle cx="40" cy="44" r="2" fill="#1A202C"/>
  <circle cx="60" cy="44" r="2" fill="#1A202C"/>
  <path d="M46 56 Q50 59 54 56" fill="none" stroke="#1A202C" stroke-width="2" stroke-linecap="round"/>
</svg>
`.trim();

const Sidebar = ({ isOpen, onClose }) => {
  const [openGroups, setOpenGroups] = useState({});
  const { user, logout } = useAuth();
  const [profilePoints, setProfilePoints] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get("/profile");
        if (response.data && response.data.data) {
          setProfilePoints(response.data.data.available_credit);
        }
      } catch (err) {
        console.error("Error fetching profile points:", err);
      }
    };

    fetchProfile();
    const interval = setInterval(fetchProfile, 10000);
    return () => clearInterval(interval);
  }, []);

  const displayPoints = () => {
    if (user?.role === "admin") {
      return "100Cr";
    }
    if (profilePoints !== null && profilePoints !== undefined) {
      return Number(profilePoints).toFixed(2);
    }
    if (user?.available_credit !== undefined && user?.available_credit !== null) {
      return Number(user.available_credit).toFixed(2);
    }
    return "0.00";
  };

  const filteredMenu = menu.filter((item) => {
    const role = user?.role;
    if (item.label === "Back Office") {
      return role === "admin";
    }
    if (item.label === "Agents") {
      return role === "admin" || role === "backoffice";
    }
    if (item.label === "Payment Methods") {
      return role === "agent";
    }
    if (item.label === "Settings") {
      return role === "agent";
    }
    if (item.label === "Recharge List") {
      return role === "agent";
    }
    if (item.label === "Withdrawal List") {
      return role === "agent";
    }
    return true;
  });

  const toggleGroup = (label) =>
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/30 z-20 md:hidden" onClick={onClose} />}

      <aside
        className={`fixed z-30 inset-y-0 left-0 w-64 transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "-translate-x-full"} md:static md:translate-x-0 flex flex-col`}
        style={{
          background: BG,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div
          style={{
            height: "64px",
            display: "flex",
            alignItems: "center",
            padding: "0 20px",
            background: "#343a40",
            borderBottom: "1px solid #4f5962",
            boxSizing: "border-box",
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: "19px",
              color: "#fff",
              letterSpacing: "0.5px",
              textTransform: "capitalize",
            }}
          >
            {user?.role}
          </span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "15px 20px",
            borderBottom: "1px solid #4f5962",
            background: BG,
          }}
        >
          <img
            src={"/avatar5.png"}
            alt="User avatar"
            style={{
              width: "45px",
              height: "45px",
              borderRadius: "50%",
              backgroundColor: "#E2E8F0",
              flexShrink: 0,
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <span
              style={{
                color: "#fff",
                fontSize: "14px",
                fontWeight: 600,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {user?.name || "testag"}
            </span>
            <span
              style={{
                color: "#b8c7ce",
                fontSize: "12px",
                marginTop: "2px",
              }}
            >
              points: {displayPoints()}
            </span>
          </div>
        </div>

        <nav
          className="sidebar-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px 10px",
          }}
        >
          {filteredMenu.map((item) => {
            const Icon = item.icon;
            const hasChildren = !!item.children?.length;
            const isGroupOpen = openGroups[item.label];

            if (!hasChildren) {
              return (
                <NavLink
                  key={item.label}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={onClose}
                  style={({ isActive }) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "12px 15px",
                    borderRadius: 4,
                    marginBottom: 3,
                    textDecoration: "none",
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? GREEN : TEXT_PRIMARY,
                    background: isActive ? GREEN_BG : "transparent",
                    transition: "all 0.15s ease",
                  })}
                  className="sb-link"
                >
                  {({ isActive }) => (
                    <>
                      <Icon style={{ fontSize: 18, color: isActive ? GREEN : TEXT_MUTED, flexShrink: 0 }} />
                      <span>{item.label}</span>
                    </>
                  )}
                </NavLink>
              );
            }

            return (
              <div key={item.label} style={{ marginBottom: 3 }}>
                <button
                  onClick={() => toggleGroup(item.label)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    width: "100%",
                    padding: "12px 15px",
                    borderRadius: 4,
                    border: "none",
                    background: "transparent",
                    color: "#b8c7ce",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.15s ease",
                  }}
                  className="sb-group"
                >
                  <Icon style={{ fontSize: 18, color: TEXT_MUTED, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <FiChevronDown
                    style={{
                      fontSize: 14,
                      color: TEXT_MUTED,
                      transform: isGroupOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </button>

                {isGroupOpen && (
                  <div
                    style={{
                      paddingLeft: 10,
                      marginTop: 2,
                      marginBottom: 4,
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      return (
                        <NavLink
                          key={child.label}
                          to={child.to}
                          onClick={onClose}
                          style={({ isActive }) => ({
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "8px 12px",
                            borderRadius: 4,
                            marginBottom: 2,
                            textDecoration: "none",
                            fontSize: 13,
                            fontWeight: isActive ? 600 : 500,
                            color: isActive ? GREEN : TEXT_PRIMARY,
                            background: isActive ? GREEN_BG : "transparent",
                            transition: "all 0.15s ease",
                          })}
                          className="sb-link"
                        >
                          {({ isActive }) => (
                            <>
                              {ChildIcon && (
                                <ChildIcon
                                  style={{
                                    fontSize: 12,
                                    color: isActive ? GREEN : TEXT_MUTED,
                                  }}
                                />
                              )}
                              <span>{child.label}</span>
                            </>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          <button
            onClick={() => {
              logout();
              onClose();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 15px",
              borderRadius: 4,
              marginBottom: 3,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              textAlign: "left",
              width: "100%",
              fontSize: 14,
              fontWeight: 500,
              color: TEXT_PRIMARY,
              transition: "all 0.15s ease",
            }}
            className="sb-link"
          >
            <FiLogOut style={{ fontSize: 18, color: TEXT_MUTED, flexShrink: 0 }} />
            <span>Logout</span>
          </button>
        </nav>
      </aside>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        .sidebar-scroll::-webkit-scrollbar { width: 4px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
        }

        .sb-link:not(.active):hover {
          background-color: #1e282c !important;
          color: #fff !important;
        }
        .sb-link:not(.active):hover svg {
          color: #fff !important;
        }

        .sb-group:hover {
          background-color: #1e282c !important;
          color: #fff !important;
        }
        .sb-group:hover svg {
          color: #fff !important;
        }
      `}</style>
    </>
  );
};

export default Sidebar;